import { useEffect, useState } from "react";

const SCRIPT_URLS = [
  "https://verify.msg91.com/otp-provider.js",
  "https://verify.phone91.com/otp-provider.js",
];

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log("[MSG91]", ...args);
};

/** Returns a promise that resolves once one of the SDK URLs loads (idempotent). */
function loadSdkOnce() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.__msg91_sdk_promise__) return window.__msg91_sdk_promise__;
  if (typeof window.initSendOTP === "function") return Promise.resolve(true);

  window.__msg91_sdk_promise__ = new Promise((resolve) => {
    let i = 0;
    const attempt = () => {
      log(`loading SDK from ${SCRIPT_URLS[i]}`);
      const s = document.createElement("script");
      s.src = SCRIPT_URLS[i];
      s.async = true;
      s.onload = () => {
        const ok = typeof window.initSendOTP === "function";
        log(`SDK script loaded: initSendOTP=${ok ? "function" : "undefined"}`);
        resolve(ok);
      };
      s.onerror = () => {
        log(`SDK script failed: ${SCRIPT_URLS[i]}`);
        i++;
        if (i < SCRIPT_URLS.length) attempt();
        else resolve(false);
      };
      document.head.appendChild(s);
    };
    attempt();
  });
  return window.__msg91_sdk_promise__;
}

/** Strip everything except digits — MSG91 wants identifier "919999999999" (no +). */
export function buildIdentifier(dialCode, nationalNumber) {
  const dial = String(dialCode || "").replace(/\D/g, "");
  const num = String(nationalNumber || "").replace(/\D/g, "");
  if (!dial || !num) return "";
  // If user pasted full international number into the field, don't double the dial code.
  if (num.startsWith(dial) && num.length >= dial.length + 6) return num;
  return `${dial}${num}`;
}

/**
 * Loads the MSG91 OTP Web SDK and calls `initSendOTP` once we have widgetId +
 * tokenAuth. Returns `ready`, `error`, and the three exposed methods
 * (`sendOtp`, `verifyOtp`, `retryOtp`) wrapped as promises.
 */
export default function useMsg91Widget({ widgetId, tokenAuth, captchaRenderId = "captcha-container" }) {
  const [ready, setReady] = useState(
    typeof window !== "undefined" && typeof window.sendOtp === "function"
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!widgetId || !tokenAuth) {
      log("config not ready yet — widgetId or tokenAuth missing");
      return undefined;
    }
    if (typeof window === "undefined") return undefined;

    // Already initialized — reuse the methods.
    if (typeof window.sendOtp === "function") {
      log("SDK already initialised — reusing window.sendOtp");
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    let pollHandle = null;

    const pollForMethods = () => {
      const started = Date.now();
      pollHandle = setInterval(() => {
        if (cancelled) { clearInterval(pollHandle); return; }
        if (typeof window.sendOtp === "function") {
          clearInterval(pollHandle);
          log("SDK ready: sendOtp / verifyOtp / retryOtp attached");
          setReady(true);
        } else if (Date.now() - started > 8000) {
          clearInterval(pollHandle);
          const msg = "OTP provider did not initialise in time";
          log("ERROR:", msg);
          setError(msg);
        }
      }, 120);
    };

    (async () => {
      const ok = await loadSdkOnce();
      if (cancelled) return;
      if (!ok || typeof window.initSendOTP !== "function") {
        setError("Could not load OTP provider");
        return;
      }

      // Guard against React StrictMode double-mount / multiple component mounts.
      if (window.__msg91_init_done__) {
        log("SDK already inited by another mount — waiting for methods");
        pollForMethods();
        return;
      }
      window.__msg91_init_done__ = true;

      try {
        log("calling initSendOTP", { widgetId, captchaRenderId });
        window.initSendOTP({
          widgetId,
          tokenAuth,
          exposeMethods: true,
          captchaRenderId,
          success: (data) => log("global success:", data),
          failure: (err) => log("global failure:", err),
        });
        pollForMethods();
      } catch (e) {
        window.__msg91_init_done__ = false;
        const msg = e?.message || "OTP init failed";
        log("init exception:", msg);
        setError(msg);
      }
    })();

    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
    };
  }, [widgetId, tokenAuth, captchaRenderId]);

  const sendOtp = (identifier) =>
    new Promise((resolve, reject) => {
      if (typeof window.sendOtp !== "function") {
        const msg = "OTP provider not ready";
        log("sendOtp aborted:", msg);
        return reject(new Error(msg));
      }
      log("sendOtp →", identifier);
      window.sendOtp(
        identifier,
        (data) => { log("sendOtp success:", data); resolve(data); },
        (err) => { log("sendOtp failure:", err); reject(err); }
      );
    });

  const verifyOtp = (otp) =>
    new Promise((resolve, reject) => {
      if (typeof window.verifyOtp !== "function") {
        const msg = "OTP provider not ready";
        log("verifyOtp aborted:", msg);
        return reject(new Error(msg));
      }
      log("verifyOtp → length", String(otp).length);
      window.verifyOtp(
        otp,
        (data) => { log("verifyOtp success:", data); resolve(data); },
        (err) => { log("verifyOtp failure:", err); reject(err); }
      );
    });

  const retryOtp = (channel = "11") =>
    new Promise((resolve, reject) => {
      if (typeof window.retryOtp !== "function") {
        const msg = "OTP provider not ready";
        log("retryOtp aborted:", msg);
        return reject(new Error(msg));
      }
      log("retryOtp → channel", channel);
      window.retryOtp(
        channel,
        (data) => { log("retryOtp success:", data); resolve(data); },
        (err) => { log("retryOtp failure:", err); reject(err); }
      );
    });

  return { ready, error, sendOtp, verifyOtp, retryOtp };
}
